/**
 * The yard — what lies outside the house: a lawn round the whole plan, a
 * concrete apron outside each garage door running out as a driveway to the
 * lawn's edge, and a landing outside each exterior door. Derived from the
 * plan, never stored: a door moved is a slab moved.
 *
 * Pure, sheet coordinates (feet), the same frame the plan's rooms are in.
 * The 3D view builds it (room3d/yardBuilder.ts) in the focus room's own
 * coordinates (`yardInRoom`), and the room export carries it to the renderer
 * as geometry, so the view and the render show the same yard.
 *
 * Exterior means: on a wall that is still there, not along a stretch two
 * rooms share, and not a door linked to the room on the other side. A slab
 * starts at the wall's INSIDE face -- the threshold -- and runs out under
 * the framing, so the floor meets the driveway with nothing between (the
 * slab lies a hair below the floor, yardBuilder.ts). An opening's `at` is
 * the framing's outer face, where what stands outside it starts.
 */

import { Opening } from '../../types/sections';
import { FloorPlan, LAWN_MARGIN_FT, PlanFace, PlanRect, PlanRoom, PLAN_FACES, WALL_GAP_FT, planBounds, sheetAlong, sharedStretches, wallIsGone } from './floorPlan';

/** How far the lawn reaches beyond the plan's bounds (feet; floorPlan.ts); the driveway runs out to its edge. */
export { LAWN_MARGIN_FT };
/** An apron is the garage door's width and this much more each side (feet). */
export const APRON_SIDE_FT = 1;
/** A landing is the door's width and this much more each side, and this deep (feet). */
export const LANDING_SIDE_FT = 1;
export const LANDING_DEPTH_FT = 4;

/** An axis-aligned rectangle on the sheet, feet. */
export interface YardRect { x0: number; z0: number; x1: number; z1: number }

/** A concrete slab: an apron (driveway) outside a garage door, or a landing outside a door. */
export interface YardSlab extends YardRect {
  kind: 'apron' | 'landing';
  roomId: string;
  face: PlanFace;
  /** The openings it serves (aprons that touched were joined). */
  openingIds: string[];
}

/** An exterior opening, for what stands outside it. */
export interface YardOpening {
  roomId: string;
  face: PlanFace;
  kind: 'window' | 'door' | 'garageDoor';
  /** The axis the wall runs along on the sheet, and the opening's span along it. */
  axis: 'x' | 'z';
  from: number;
  to: number;
  /** The framing's outer face, across the wall (the other sheet axis). */
  at: number;
  /** Feet up: the opening's bottom and top. */
  sill: number;
  head: number;
  /** Which way is out: a unit step on the sheet. */
  outward: { x: number; z: number };
}

export interface Yard {
  lawn: YardRect;
  slabs: YardSlab[];
  /** Every room's footprint to the outer face of its framing. */
  footprints: YardRect[];
  openings: YardOpening[];
}

export const EMPTY_YARD: Yard = { lawn: { x0: 0, z0: 0, x1: 0, z1: 0 }, slabs: [], footprints: [], openings: [] };

const grown = (r: PlanRect, by: number): YardRect => ({ x0: r.x - by, z0: r.z - by, x1: r.x + r.length + by, z1: r.z + r.width + by });

/** Where a face's framing has its outer face on the sheet (`at`) and the wall its inside face (`inner`), which axis the wall runs along, and which way is out. */
function faceLine(room: PlanRect, face: PlanFace): { axis: 'x' | 'z'; at: number; inner: number; outward: { x: number; z: number } } {
  switch (face) {
    case 'front': return { axis: 'x', at: room.z - WALL_GAP_FT, inner: room.z, outward: { x: 0, z: -1 } };
    case 'back': return { axis: 'x', at: room.z + room.width + WALL_GAP_FT, inner: room.z + room.width, outward: { x: 0, z: 1 } };
    case 'left': return { axis: 'z', at: room.x - WALL_GAP_FT, inner: room.x, outward: { x: -1, z: 0 } };
    case 'right': return { axis: 'z', at: room.x + room.length + WALL_GAP_FT, inner: room.x + room.length, outward: { x: 1, z: 0 } };
  }
}

/** A slab outside an opening: `from..to` along the wall, from the wall's inside face (the threshold) `depth` feet out, clipped to the lawn. */
function slabOutside(line: ReturnType<typeof faceLine>, from: number, to: number, depth: number, lawn: YardRect): YardRect | null {
  let r: YardRect;
  if (line.axis === 'x') {
    const out = line.outward.z < 0 ? [line.inner - depth, line.inner] : [line.inner, line.inner + depth];
    r = { x0: from, x1: to, z0: out[0], z1: out[1] };
  } else {
    const out = line.outward.x < 0 ? [line.inner - depth, line.inner] : [line.inner, line.inner + depth];
    r = { x0: out[0], x1: out[1], z0: from, z1: to };
  }
  const c: YardRect = { x0: Math.max(r.x0, lawn.x0), z0: Math.max(r.z0, lawn.z0), x1: Math.min(r.x1, lawn.x1), z1: Math.min(r.z1, lawn.z1) };
  return c.x1 - c.x0 > 0.01 && c.z1 - c.z0 > 0.01 ? c : null;
}

const spans = (r: YardRect, axis: 'x' | 'z'): [number, number] => (axis === 'x' ? [r.x0, r.x1] : [r.z0, r.z1]);
const overlapsSpan = (a: [number, number], b: [number, number]) => a[0] <= b[1] + 1e-6 && a[1] >= b[0] - 1e-6;

/** The yard of a plan. A blank plan has none (EMPTY_YARD). */
export function yardOf(plan: FloorPlan): Yard {
  const bounds = planBounds(plan);
  if (!bounds) return EMPTY_YARD;
  const lawn = grown(bounds, LAWN_MARGIN_FT + WALL_GAP_FT);
  const footprints = plan.rooms.map((r) => grown(r, WALL_GAP_FT));
  const shared = sharedStretches(plan);
  const openings: YardOpening[] = [];
  // Every slab as cut, by wall line, before touching aprons are joined.
  const cut: YardSlab[] = [];
  for (const room of plan.rooms) {
    for (const face of PLAN_FACES) {
      if (wallIsGone(plan, room, face)) continue;
      const wall = room.surfaces.walls[PLAN_FACES.indexOf(face)];
      const line = faceLine(room, face);
      // The stretches of this wall another room stands against: nothing is outside there.
      const sharedHere = shared.filter((s) => (s.owner.roomId === room.id && s.owner.face === face) || (s.other.roomId === room.id && s.other.face === face));
      for (const o of wall.openings ?? []) {
        if (o.kind === 'passage' || o.link) continue;
        const a = sheetAlong(room, face, o.x), b = sheetAlong(room, face, o.x + o.width);
        const from = Math.min(a, b), to = Math.max(a, b);
        if (sharedHere.some((s) => overlapsSpan([from, to], [s.from, s.to]))) continue;
        openings.push({ roomId: room.id, face, kind: o.kind, axis: line.axis, from, to, at: line.at, sill: o.y, head: o.y + o.height, outward: line.outward });
        const slab = slabFor(o, line, from, to, lawn);
        if (slab) cut.push({ ...slab, kind: o.kind === 'garageDoor' ? 'apron' : 'landing', roomId: room.id, face, openingIds: [o.id] });
      }
    }
  }
  return { lawn, slabs: joinSlabs(cut), footprints, openings };
}

function slabFor(o: Opening, line: ReturnType<typeof faceLine>, from: number, to: number, lawn: YardRect): YardRect | null {
  if (o.kind === 'garageDoor') return slabOutside(line, from - APRON_SIDE_FT, to + APRON_SIDE_FT, LAWN_MARGIN_FT * 2, lawn); // to the lawn's edge
  if (o.kind === 'door') return slabOutside(line, from - LANDING_SIDE_FT, to + LANDING_SIDE_FT, LANDING_DEPTH_FT + WALL_GAP_FT, lawn);
  return null;
}

/**
 * Slabs on one wall line that touch or overlap, joined: aprons into one
 * driveway; a landing an apron already covers is dropped; landings that
 * overlap each other are joined.
 */
function joinSlabs(cut: YardSlab[]): YardSlab[] {
  const byLine = new Map<string, YardSlab[]>();
  for (const s of cut) {
    const axis = s.face === 'front' || s.face === 'back' ? 'x' : 'z';
    const at = axis === 'x' ? (s.face === 'front' ? s.z1 : s.z0) : (s.face === 'left' ? s.x1 : s.x0);
    const key = `${axis}:${at.toFixed(4)}:${s.face}`;
    byLine.set(key, [...(byLine.get(key) ?? []), s]);
  }
  const out: YardSlab[] = [];
  byLine.forEach((list, key) => {
    const axis = key[0] as 'x' | 'z';
    const join = (slabs: YardSlab[]): YardSlab[] => {
      const sorted = [...slabs].sort((a, b) => spans(a, axis)[0] - spans(b, axis)[0]);
      const joined: YardSlab[] = [];
      for (const s of sorted) {
        const last = joined[joined.length - 1];
        if (last && overlapsSpan(spans(last, axis), spans(s, axis))) {
          last.x0 = Math.min(last.x0, s.x0); last.z0 = Math.min(last.z0, s.z0);
          last.x1 = Math.max(last.x1, s.x1); last.z1 = Math.max(last.z1, s.z1);
          last.openingIds = [...last.openingIds, ...s.openingIds];
        } else joined.push({ ...s, openingIds: [...s.openingIds] });
      }
      return joined;
    };
    const aprons = join(list.filter((s) => s.kind === 'apron'));
    const landings = join(list.filter((s) => s.kind === 'landing' && !aprons.some((a) => overlapsSpan(spans(a, axis), spans(s, axis)))));
    out.push(...aprons, ...landings);
  });
  return out;
}

/** The yard in a room's own coordinates (its front-left inside corner at the origin). */
export function yardInRoom(yard: Yard, room: Pick<PlanRect, 'x' | 'z'>): Yard {
  const r = (q: YardRect): YardRect => ({ x0: q.x0 - room.x, z0: q.z0 - room.z, x1: q.x1 - room.x, z1: q.z1 - room.z });
  return {
    lawn: r(yard.lawn),
    slabs: yard.slabs.map((s) => ({ ...s, ...r(s) })),
    footprints: yard.footprints.map(r),
    openings: yard.openings.map((o) => {
      const along = o.axis === 'x' ? room.x : room.z, across = o.axis === 'x' ? room.z : room.x;
      return { ...o, from: o.from - along, to: o.to - along, at: o.at - across };
    }),
  };
}

/** Is a sheet point on any slab? */
export function onSlab(yard: Yard, x: number, z: number): boolean {
  return yard.slabs.some((s) => x >= s.x0 && x <= s.x1 && z >= s.z0 && z <= s.z1);
}

export type { PlanRoom as YardRoom };
